/**
 * useCatalogItems — lightweight per-tenant Catalog Items hook.
 *
 * REST-based (not a Firestore listener) — the catalog is bounded and
 * rarely mutates, so a single GET on first mount + explicit refresh is
 * far cheaper than a live snapshot for a screen most users never open.
 *
 * Module-level cache keyed by companyId. Any component that calls
 * refresh() bumps a shared version so all other mounted consumers
 * re-fetch.
 */
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useCompany } from '../context/CompanyContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Module-scoped cache + subscription registry ────────────────────────────

const cache = new Map(); // companyId → array of items
const inflight = new Map(); // companyId → Promise
let version = 0;
const listeners = new Set();

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function getSnapshot() { return version; }
function bumpVersion() { version += 1; listeners.forEach(l => l()); }

async function fetchItems(companyId) {
  if (!companyId) return [];
  if (inflight.has(companyId)) return inflight.get(companyId);
  const p = (async () => {
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.get(`${API}/api/catalog-items/`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = Array.isArray(res.data) ? res.data : [];
      cache.set(companyId, items);
      bumpVersion();
      return items;
    } finally {
      inflight.delete(companyId);
    }
  })();
  inflight.set(companyId, p);
  return p;
}

export function useCatalogItems() {
  const { companyId } = useCompany();
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [loading, setLoading] = useState(!cache.has(companyId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    if (cache.has(companyId)) { setLoading(false); return; }
    setLoading(true);
    fetchItems(companyId)
      .then(() => setLoading(false))
      .catch((e) => { setError(e); setLoading(false); });
  }, [companyId]);

  const refresh = useCallback(async () => {
    cache.delete(companyId);
    setLoading(true);
    try {
      await fetchItems(companyId);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const items = cache.get(companyId) || [];
  const byId = {};
  for (const it of items) byId[it.id] = it;
  const activeItems = items.filter(i => i.active !== false);

  return { items, byId, activeItems, loading, error, refresh };
}

export function invalidateCatalog(companyId) {
  if (companyId) cache.delete(companyId);
  bumpVersion();
}
